import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import ProductList from '../components/ProductList';
import { fetchCategories, buildCategoryTree } from '../lib/categories';
import { fetchSuppliersPublic } from '../lib/suppliers';
import { getFileUrl } from '../lib/pb';

export default function Catalogue() {
  const [categories, setCategories] = useState([]);
  const [catsLoading, setCatsLoading] = useState(false);
  const [catsError, setCatsError] = useState('');
  const [selectedFilterIds, setSelectedFilterIds] = useState(() => {
    try {
      const raw = localStorage.getItem('catalogue.selectedFilterIds');
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  });
  const [selectedRootId, setSelectedRootId] = useState(() => localStorage.getItem('catalogue.selectedRootId') || '');
  const [expandedIds, setExpandedIds] = useState([]);
  const [productsCount, setProductsCount] = useState(0);
  const [suppliers, setSuppliers] = useState([]);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [sortMode, setSortMode] = useState(() => localStorage.getItem('catalogue.sortMode') || 'random');
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const productsTopRef = useRef(null);
  const skipScrollRef = useRef(true);

  useEffect(() => {
    let cancelled = false;
    async function loadCats() {
      setCatsLoading(true);
      setCatsError('');
      try {
        const list = await fetchCategories('catalogue');
        if (!cancelled) {
          const safe = list || [];
          setCategories(safe);
          // replier par défaut
          setExpandedIds([]);
        }
      } catch (e) {
        if (!cancelled) {
          setCatsError(e.message || 'Erreur lors du chargement des catégories');
        }
      } finally {
        if (!cancelled) {
          setCatsLoading(false);
        }
      }
    }
    loadCats();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 700);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // When categories load, if we have a saved root id but no selected ids, rebuild them from the tree
  useEffect(() => {
    if (!categories.length) return;
    const savedRoot = localStorage.getItem('catalogue.selectedRootId') || '';
    let savedIds = [];
    try {
      const raw = localStorage.getItem('catalogue.selectedFilterIds');
      savedIds = raw ? JSON.parse(raw) : [];
    } catch { savedIds = []; }
    const treeNow = buildCategoryTree(categories);
    if (savedRoot && (!Array.isArray(savedIds) || !savedIds.length)) {
      const findById = (nodes, targetId) => {
        for (const n of nodes) {
          if (n.id === targetId) return n;
          const child = n.children && n.children.length ? findById(n.children, targetId) : null;
          if (child) return child;
        }
        return null;
      };
      const node = findById(treeNow, savedRoot);
      if (node) {
        const ids = [node.id, ...collectDescendantIds(node)];
        setSelectedFilterIds(ids);
        setExpandedIds(prev => Array.from(new Set([...prev, ...ids])));
      }
    } else if (Array.isArray(savedIds) && savedIds.length) {
      setExpandedIds(prev => Array.from(new Set([...prev, ...savedIds])));
    }
    const t = setTimeout(() => { skipScrollRef.current = false; }, 200);
    return () => clearTimeout(t);
  }, [categories]);

  // Persist to localStorage on change
  useEffect(() => {
    try { localStorage.setItem('catalogue.sortMode', sortMode); } catch { }
  }, [sortMode]);
  useEffect(() => {
    try { localStorage.setItem('catalogue.selectedRootId', selectedRootId || ''); } catch { }
  }, [selectedRootId]);
  useEffect(() => {
    try { localStorage.setItem('catalogue.selectedFilterIds', JSON.stringify(selectedFilterIds || [])); } catch { }
  }, [selectedFilterIds]);

  useEffect(() => {
    let cancelled = false;
    async function loadSuppliers() {
      try {
        const list = await fetchSuppliersPublic();
        if (!cancelled) {
          setSuppliers(list || []);
        }
      } catch (e) {
        // on ignore discrètement l'erreur côté catalogue
      }
    }
    loadSuppliers();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (skipScrollRef.current) return;
    if (productsTopRef.current) {
      const offset = 140; // Hauteur approximative du header
      const elementPosition = productsTopRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  }, [selectedFilterIds, sortMode]);

  const tree = buildCategoryTree(categories);
  const rootCategories = tree;

  return (
    <div className="catalogue-page-signature">
      <div className="container-catalogue-premium">
        
        {/* HEADER SECTION */}
        <header className="catalogue-header-main">
           <div className="tag-line-premium">AEF 32 • SÉCURITÉ & INNOVATION</div>
           <h1>Catalogue</h1>
           <p className="catalogue-subtitle">
             Découvrez l'excellence de nos gammes de portails, automatismes et fermetures.
           </p>
        </header>

        {/* ROOT CATEGORIES GRID (GAMMES) */}
        <section className="gammes-section-signature">
          <div className="section-title-box centered-titles">
             <div className="accent-bar-horiz"></div>
             <h2>Nos gammes de produits</h2>
          </div>
          {catsLoading && <div className="loader-signature">Chargement des catégories…</div>}
          {catsError && !catsLoading && <div className="error-signature">{catsError}</div>}
          {!catsLoading && !catsError && !!rootCategories.length && (
            <div className="gammes-grid-signature">
              {rootCategories.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  className="gamme-card-royale shadow-signature"
                  onClick={() => cat.slug && navigate(`/catalogue/${cat.slug}`)}
                >
                  <div className="gamme-visual">
                    {cat.image ? (
                      <img src={getFileUrl(cat, cat.image)} alt={cat.name} />
                    ) : (
                      <div className="placeholder-gamme">🛋️</div>
                    )}
                    <div className="gamme-overlay-soft"></div>
                  </div>
                  <div className="gamme-footer">
                     <span>{cat.name}</span>
                     <div className="arrow-gamme">→</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* MAIN CATALOGUE AREA */}
        <section ref={productsTopRef} className="main-catalog-area">
          <div className="catalog-toolbar-signature shadow-signature">
            <div className="toolbar-left">
              <h2>Showroom détaillé</h2>
              <div className="status-pill-signature">
                 <span className="dot-live"></span> {productsCount} modèles disponibles
              </div>
            </div>
            
            <div className="toolbar-right">
              <div className="tool-select-group">
                <span className="tool-label">Trier par</span>
                <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}>
                  <option value="random">Aléatoire</option>
                  <option value="relevance">Pertinence</option>
                  <option value="name-asc">Nom (A → Z)</option>
                  <option value="name-desc">Nom (Z → A)</option>
                </select>
              </div>

              <div className="tool-select-group">
                <span className="tool-label">Secteur</span>
                <select 
                  value={selectedRootId} 
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) { setSelectedFilterIds([]); setSelectedRootId(''); return; }
                    const findById = (nodes, id) => {
                      for (const n of nodes) {
                        if (n.id === id) return n;
                        if (n.children?.length) { const r = findById(n.children, id); if(r) return r; }
                      }
                      return null;
                    };
                    const node = findById(rootCategories, value);
                    if (!node) { setSelectedFilterIds([]); setSelectedRootId(''); return; }
                    const ids = [node.id, ...collectDescendantIds(node)];
                    setSelectedFilterIds(ids);
                    setSelectedRootId(node.id);
                    setExpandedIds(prev => Array.from(new Set([...prev, ...ids])));
                  }}
                >
                  <option value="">Toutes nos gammes</option>
                  {categories.filter(c => !c.parent).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="mobile-filter-trigger-wrap">
            <button className="btn-mobile-filter-emerald" onClick={() => setIsMobileFilterOpen(true)}>
               <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 3H2l8 9v6l4 3v-9L22 3z"/></svg>
               Filtrer le catalogue
            </button>
          </div>

          <div className="catalog-studio-layout">
            {/* SIDEBAR FILTERS */}
            <aside className="catalog-sidebar-signature shadow-signature">
              <div className="sidebar-header-pro">
                 <h3>Console de filtres</h3>
                 {selectedFilterIds.length > 0 && (
                   <button className="btn-reset-sidebar" onClick={() => { setSelectedFilterIds([]); setSelectedRootId(''); localStorage.removeItem('catalogue.selectedFilterIds'); localStorage.removeItem('catalogue.selectedRootId'); }}>
                      Reset
                   </button>
                 )}
              </div>
              <div className="sidebar-scroll-area">
                {tree.map(node => (
                  <FilterNode
                    key={node.id}
                    node={node}
                    level={0}
                    selectedIds={selectedFilterIds}
                    expandedIds={expandedIds}
                    onToggle={(ids) => {
                      setSelectedFilterIds(prev => {
                        const allSelected = ids.every(id => prev.includes(id));
                        if (allSelected) return prev.filter(x => !ids.includes(x));
                        const next = [...prev];
                        ids.forEach(id => { if (!next.includes(id)) next.push(id); });
                        return next;
                      });
                      setExpandedIds(prev => Array.from(new Set([...prev, ...ids])));
                    }}
                    onToggleExpand={(id) => {
                      setExpandedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
                    }}
                  />
                ))}
              </div>
            </aside>

            {/* PRODUCT GRID */}
            <main className="catalog-main-content">
              {selectedFilterIds.length > 0 && (
                <div className="active-filters-info">
                   <span>Affichage filtré par catégories selectionnées</span>
                   <button onClick={() => setSelectedFilterIds([])}>Tout afficher ×</button>
                </div>
              )}
              <ProductList
                categoryId={null}
                categoryIds={selectedFilterIds}
                onCountChange={setProductsCount}
                sortMode={sortMode}
              />
            </main>
          </div>
        </section>

        {/* MOBILE OVERLAY */}
        {isMobileFilterOpen && (
          <div className="mobile-filter-overlay-signature">
            <div className="overlay-backdrop-blur" onClick={() => setIsMobileFilterOpen(false)}></div>
            <div className="mobile-filter-sheet animated-slide-up">
              <div className="sheet-header">
                <div>
                  <h4>Filtres Catalogue</h4>
                  <p>Affinez votre sélection</p>
                </div>
                <button className="btn-close-sheet" onClick={() => setIsMobileFilterOpen(false)}>×</button>
              </div>
              <div className="sheet-scroll-area">
                {tree.map(node => (
                  <FilterNode
                    key={node.id}
                    node={node}
                    level={0}
                    selectedIds={selectedFilterIds}
                    expandedIds={expandedIds}
                    onToggle={(ids) => {
                      setSelectedFilterIds(prev => {
                        const allSelected = ids.every(id => prev.includes(id));
                        if (allSelected) return prev.filter(x => !ids.includes(x));
                        const next = [...prev];
                        ids.forEach(id => { if (!next.includes(id)) next.push(id); });
                        return next;
                      });
                      setExpandedIds(prev => Array.from(new Set([...prev, ...ids])));
                    }}
                    onToggleExpand={(id) => {
                      setExpandedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
                    }}
                  />
                ))}
              </div>
              <div className="sheet-footer">
                 <button className="btn-apply-sheet" onClick={() => setIsMobileFilterOpen(false)}>
                    Appliquer le filtre ({productsCount})
                 </button>
              </div>
            </div>
          </div>
        )}

        {/* FOOTER SUPPLIERS */}
        {!!suppliers.length && (
          <section className="catalog-suppliers-band">
             <div className="container-about-premium">
                <h2 className="title-signature-mini">Nos partenaires techniques</h2>
                <div className="suppliers-pills-row">
                   {suppliers.map(s => (
                     <div key={s.id} className="supplier-pill shadow-signature">
                        {s.logo ? <img src={getFileUrl(s, s.logo)} alt={s.name} /> : <span>{s.name}</span>}
                     </div>
                   ))}
                </div>
             </div>
          </section>
        )}

        <style>{`
          .catalogue-page-signature { 
             background: #fff; padding: 40px 0; min-height: 100vh;
             font-family: 'Inter', sans-serif; color: #0f172a; 
          }
          .container-catalogue-premium { max-width: 1200px; margin: 0 auto; padding: 0 20px; }

          /* HEADER */
          .catalogue-header-main { text-align: center; margin-bottom: 60px; }
          .tag-line-premium { font-size: 11px; font-weight: 800; color: var(--accent); letter-spacing: 2.5px; margin-bottom: 15px; }
          .catalogue-header-main h1 { font-size: clamp(32px, 5vw, 48px); font-weight: 900; margin: 0 0 15px 0; letter-spacing: -1.5px; }
          .catalogue-subtitle { color: #64748b; font-size: 17px; max-width: 600px; margin: 0 auto; line-height: 1.6; }

          /* GAMMES GRID */
          .gammes-section-signature { margin-bottom: 80px; }
          .centered-titles { text-align: center; display: flex; flex-direction: column; align-items: center; gap: 10px; margin-bottom: 30px; }
          .accent-bar-horiz { width: 40px; height: 3px; background: var(--accent); border-radius: 4px; }
          .section-title-box h2 { font-size: 24px; font-weight: 900; margin: 0; }
          
          .gammes-grid-signature { display: flex; flex-wrap: wrap; justify-content: center; gap: 20px; }
          .gamme-card-royale { 
             flex: 0 1 200px; background: #fff; border-radius: 20px; border: 1px solid #f1f5f9;
             padding: 10px; cursor: pointer; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
             display: flex; flex-direction: column;
          }
          .gamme-card-royale:hover { transform: translateY(-5px); border-color: var(--accent); box-shadow: 0 20px 40px rgba(34, 197, 94, 0.08); }
          .gamme-visual { height: 120px; border-radius: 12px; overflow: hidden; position: relative; background: #f8fafc; }
          .gamme-visual img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s ease; }
          .gamme-card-royale:hover .gamme-visual img { transform: scale(1.1); }
          .gamme-overlay-soft { position: absolute; inset: 0; background: linear-gradient(135deg, rgba(34,197,94,0.05), transparent); }
          
          .gamme-footer { 
             padding: 12px 5px 5px 5px; display: flex; justify-content: space-between; align-items: center; 
             font-size: 14px; font-weight: 800; color: #0f172a;
          }
          .arrow-gamme { color: var(--accent); opacity: 0; transform: translateX(-10px); transition: all 0.3s; }
          .gamme-card-royale:hover .arrow-gamme { opacity: 1; transform: translateX(0); }

          /* CATALOGUE AREA */
          .catalog-toolbar-signature { 
             background: #fff; border-radius: 24px; border: 1px solid #f1f5f9; padding: 25px 35px;
             display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px;
          }
          .toolbar-left h2 { font-size: 20px; font-weight: 900; margin: 0 0 5px 0; }
          .status-pill-signature { 
             display: flex; align-items: center; gap: 10px; font-size: 12px; 
             font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 1px;
          }
          .dot-live { width: 7px; height: 7px; background: #10b981; border-radius: 50%; box-shadow: 0 0 10px #10b981; }

          .toolbar-right { display: flex; gap: 25px; }
          .tool-select-group { display: flex; align-items: center; gap: 10px; }
          .tool-label { font-size: 12px; font-weight: 800; color: #94a3b8; text-transform: uppercase; }
          .tool-select-group select { 
             border: 1px solid #e2e8f0; border-radius: 12px; padding: 8px 15px; 
             font-size: 13px; font-weight: 700; color: #0f172a; outline: none; background: #fff;
             cursor: pointer; transition: all 0.2s;
          }
          .tool-select-group select:hover { border-color: var(--accent); }

          /* STUDIO LAYOUT */
          .catalog-studio-layout { display: grid; grid-template-columns: 320px 1fr; gap: 40px; align-items: flex-start; }
          .catalog-sidebar-signature { 
             position: sticky; top: 140px; background: #fff; border-radius: 28px; 
             border: 1px solid #f1f5f9; padding: 30px; overflow: hidden;
             max-height: calc(100vh - 180px); display: flex; flex-direction: column;
          }
          .sidebar-header-pro { display: flex; justify-content: space-between; align-items: center; margin-bottom: 25px; }
          .sidebar-header-pro h3 { font-size: 16px; font-weight: 950; text-transform: uppercase; letter-spacing: 1.5px; margin: 0; color: var(--accent); }
          .btn-reset-sidebar { 
             background: #f8fafc; border: none; padding: 5px 12px; border-radius: 8px; 
             font-size: 11px; font-weight: 800; color: #64748b; cursor: pointer; transition: all 0.2s;
          }
          .btn-reset-sidebar:hover { background: #fee2e2; color: #ef4444; }
          .sidebar-scroll-area { flex-grow: 1; overflow-y: auto; padding-right: 10px; }

          .active-filters-info { 
             background: #f0fdf4; border: 1px solid rgba(34, 197, 94, 0.1); padding: 12px 20px; 
             border-radius: 14px; margin-bottom: 25px; display: flex; justify-content: space-between; 
             align-items: center; font-size: 13px; font-weight: 700; color: #166534;
          }
          .active-filters-info button { background: none; border: none; font-weight: 800; cursor: pointer; color: var(--accent); }

          /* MOBILE OVERLAY */
          .mobile-filter-trigger-wrap { display: none; margin-bottom: 20px; }
          .btn-mobile-filter-emerald { 
             width: 100%; background: var(--accent); color: #fff; border: none; padding: 15px; 
             border-radius: 16px; font-weight: 800; display: flex; align-items: center; 
             justify-content: center; gap: 10px; box-shadow: 0 10px 20px rgba(34, 197, 94, 0.2);
          }
          
          .mobile-filter-overlay-signature { position: fixed; inset: 0; z-index: 1000; }
          .overlay-backdrop-blur { position: absolute; inset: 0; background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(10px); }
          .mobile-filter-sheet { 
             position: absolute; bottom: 0; left: 0; right: 0; background: #fff; 
             border-radius: 32px 32px 0 0; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden;
          }
          .animated-slide-up { animation: slideUp 0.4s cubic-bezier(0.165, 0.84, 0.44, 1); }
          @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }

          .sheet-header { padding: 30px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
          .sheet-header h4 { font-size: 20px; font-weight: 900; margin: 0; }
          .sheet-header p { font-size: 13px; color: #64748b; margin: 5px 0 0 0; }
          .btn-close-sheet { width: 40px; height: 40px; border-radius: 50%; background: #f1f5f9; border: none; font-size: 24px; cursor: pointer; }
          .sheet-scroll-area { flex-grow: 1; overflow-y: auto; padding: 25px; }
          .sheet-footer { padding: 25px; border-top: 1px solid #f1f5f9; }
          .btn-apply-sheet { 
             width: 100%; background: #0f172a; color: #fff; border: none; padding: 18px; 
             border-radius: 16px; font-weight: 800; font-size: 15px;
          }

          /* SUPPLIERS BAND */
          .catalog-suppliers-band { padding: 60px 0; border-top: 1px solid #f1f5f9; margin-top: 80px; }
          .title-signature-mini { text-align: center; font-size: 18px; font-weight: 900; color: #0f172a; margin-bottom: 30px; letter-spacing: -0.5px; }

          @media (max-width: 1024px) {
             .catalog-studio-layout { grid-template-columns: 1fr; }
             .catalog-sidebar-signature { display: none; }
             .mobile-filter-trigger-wrap { display: block; }
             .catalog-toolbar-signature { flex-direction: column; gap: 20px; align-items: flex-start; }
             .toolbar-right { width: 100%; justify-content: space-between; }
          }
          @media (max-width: 640px) {
             .gamme-card-royale { flex: 0 1 calc(50% - 10px); }
             .toolbar-right { flex-direction: column; gap: 15px; }
          }
        `}</style>
      </div>
    </div>
  );
}

function collectDescendantIds(node) {
  const result = [];
  const stack = [...(node.children || [])];
  while (stack.length) {
    const current = stack.pop();
    if (!current) continue;
    result.push(current.id);
    if (current.children && current.children.length) {
      stack.push(...current.children);
    }
  }
  return result;
}

function FilterNode({ node, level, selectedIds, expandedIds, onToggle, onToggleExpand }) {
  const hasChildren = (node.children || []).length > 0;
  const isSelected = selectedIds.includes(node.id);
  const isExpanded = hasChildren && expandedIds.includes(node.id);

  return (
    <div className={`filter-node-wrap level-${level} ${isSelected ? 'is-selected' : ''}`}>
      <div className="filter-row-main">
        {hasChildren ? (
          <button
            type="button"
            className={`btn-expand-node ${isExpanded ? 'open' : ''}`}
            onClick={() => onToggleExpand(node.id)}
          >
            {isExpanded ? '−' : '+'}
          </button>
        ) : (
          <span className="node-spacer-dot"></span>
        )}
        
        <div 
          className={`filter-clickable-area ${isSelected ? 'active' : ''}`}
          onClick={() => onToggle([node.id, ...collectDescendantIds(node)])}
        >
          <div className="custom-check-box">
             {isSelected && <div className="check-inner"></div>}
          </div>
          <span className="node-name">{node.name}</span>
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div className="node-children-container">
          {node.children.map(child => (
            <FilterNode
              key={child.id}
              node={child}
              level={level + 1}
              selectedIds={selectedIds}
              expandedIds={expandedIds}
              onToggle={onToggle}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}

      <style>{`
        .filter-node-wrap { margin-bottom: 4px; }
        .filter-row-main { display: flex; align-items: center; gap: 8px; }
        
        .btn-expand-node { 
           width: 22px; height: 22px; border: 1px solid #e2e8f0; border-radius: 6px; 
           background: #fff; font-size: 14px; color: #64748b; cursor: pointer;
           display: flex; align-items: center; justify-content: center; transition: all 0.2s;
        }
        .btn-expand-node:hover { border-color: var(--accent); color: var(--accent); }
        .btn-expand-node.open { background: #f8fafc; }
        
        .node-spacer-dot { width: 22px; height: 22px; display: flex; align-items: center; justify-content: center; position: relative; }
        .node-spacer-dot::after { content: ''; width: 4px; height: 4px; background: #e2e8f0; border-radius: 50%; }

        .filter-clickable-area { 
           display: flex; align-items: center; gap: 10px; cursor: pointer; flex-grow: 1; 
           padding: 6px 12px; border-radius: 10px; transition: all 0.2s;
        }
        .filter-clickable-area:hover { background: #f8fafc; }
        .filter-clickable-area.active { background: #f0fdf4; }
        
        .custom-check-box { 
           width: 16px; height: 16px; border: 2px solid #e2e8f0; border-radius: 5px; 
           display: flex; align-items: center; justify-content: center; transition: all 0.2s;
        }
        .filter-clickable-area.active .custom-check-box { border-color: var(--accent); background: var(--accent); }
        .check-inner { width: 6px; height: 6px; background: #fff; border-radius: 1px; }

        .node-name { font-size: 14px; font-weight: 600; color: #475569; transition: color 0.2s; }
        .filter-clickable-area.active .node-name { color: var(--accent); font-weight: 800; }
        .filter-clickable-area:hover .node-name { color: #0f172a; }

        .node-children-container { 
           margin-left: 11px; padding-left: 15px; border-left: 1px solid #f1f5f9; 
           margin-top: 5px; margin-bottom: 10px;
        }
        
        .level-0 > .filter-row-main .node-name { font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; font-size: 13px; }
      `}</style>
    </div>
  );
}
